import { z } from 'zod';
import { DemoDataset, DemoRole, DemoSpecialty, DemoVolume, DEMO_LIMITS, GarageKind } from './demo.js';
import { ModuleKey } from './modules.js';
import { SUPPORT_SCOPES } from '../permissions.js';
import { BrandMode, CountryCode, Email, HexColor, Id, LongText, Money, Phone, ShortText, StorageDayType, Timestamp } from './common.js';
import { ActivityEntry } from './dossiers.js';

/**
 * Vague 5 : console plateforme, adresses par garage et accès support avec consentement.
 *
 * Exception documentée à « aucune entrée ne désigne un garage » : **dans l'espace `platform`
 * seulement** (administrateur plateforme), un garage est désigné par son `slug` (chemin ou corps).
 * Aucune route de l'espace `garage` n'accepte de slug ni d'identifiant de garage : le garage actif
 * vient toujours de la session.
 */

// ─── Adresse du garage ──────────────────────────────────────────────────────

export const GARAGE_SLUG_MIN = 3;
export const GARAGE_SLUG_MAX = 40;
export const GARAGE_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Identifiant du garage dans l'adresse `/g/<slug>/…` : sert à naviguer, ne donne aucun droit. */
export const GarageSlug = z
  .string()
  .trim()
  .min(GARAGE_SLUG_MIN)
  .max(GARAGE_SLUG_MAX)
  .regex(GARAGE_SLUG_PATTERN, 'Lettres minuscules sans accent, chiffres et tirets simples');
export type GarageSlug = z.infer<typeof GarageSlug>;

/**
 * Slug de base dérivé d'un nom (sans accent, minuscules, tirets), tronqué à `GARAGE_SLUG_MAX`.
 * Trop court : préfixé par `garage-`. Les collisions sont résolues par `slugCandidate`.
 */
export function slugify(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/œ/gi, 'oe')
    .replace(/æ/gi, 'ae')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const padded = base.length >= GARAGE_SLUG_MIN ? base : base ? `garage-${base}` : 'garage';
  return padded.slice(0, GARAGE_SLUG_MAX).replace(/-+$/g, '');
}

/** Candidat n° `n` (1 = le slug de base, puis `-2`, `-3`…), toujours conforme à `GarageSlug`. */
export function slugCandidate(base: string, n: number): string {
  if (n <= 1) return base;
  const suffix = `-${n}`;
  return `${base.slice(0, GARAGE_SLUG_MAX - suffix.length).replace(/-+$/g, '')}${suffix}`;
}

export const GarageStatus = z.enum(['active', 'suspended']);
export type GarageStatus = z.infer<typeof GarageStatus>;

/** Codes d'erreur dédiés (corps `{ error: { code, message } }`). */
export const ERROR_CODES = {
  /** Membre d'un garage suspendu (403) : message clair, aucune donnée du garage. */
  garageSuspended: 'garage_suspended',
  /** Portail d'un garage suspendu : message neutre, sans détail. */
  portalUnavailable: 'portal_unavailable',
  /** Session support dont l'accès n'est plus accordé (expiré, retiré, terminé) : 403 et sortie. */
  supportAccessEnded: 'support_access_ended',
  /** Écriture tentée par une session support en lecture seule. */
  supportReadOnly: 'support_read_only',
  /** Lecture de plaque : modèle absent ou désactivé (503). */
  plateScanUnavailable: 'plate_scan_unavailable',
  /** Vague 6 : photo du client sans son accord (422). */
  customerPhotoConsentRequired: 'customer_photo_consent_required',
  /** Vague 6 : effacement refusé tant qu'une intervention du client n'est pas clôturée (409). */
  customerHasOpenIntervention: 'customer_has_open_intervention',
  /** Vague 6 : fiche déjà effacée (409 en écriture). */
  customerErased: 'customer_erased',
  /** Vague 6 (S28) : trop de traitements d'image en attente (503, en-tête `Retry-After`). */
  serverBusy: 'server_busy',
  /** Vague 6 : lecture de plaque, toutes les places du sémaphore des images prises (503, `Retry-After: 1`) ; l'interface passe à l'image suivante. */
  plateScanBusy: 'plate_scan_busy',
  /** Vague 6 : PDF de la fiche de réception ni stocké ni générable (503) : « PDF bientôt disponible ». */
  entryReportPdfUnavailable: 'entry_report_pdf_unavailable',
  /**
   * Correctifs (audit S35) : l'en-tête `x-garage-expected` (slug de l'adresse `/g/:slug`) ne correspond pas
   * au garage actif de la session (409, sans rien révéler) ; l'interface relit `auth.me`.
   */
  garageMismatch: 'garage_mismatch',
  /** Vague 7 : écriture propre à un module désactivé dans le garage (403). */
  moduleDisabled: 'module_disabled',
  /** Vague 7 : action impossible en démonstration (invitation, mot de passe, marque, console…) (403). */
  demoForbidden: 'demo_forbidden',
  /** Vague 7 : plafond de la session de démonstration atteint (dossiers, photos) (429). */
  demoQuotaReached: 'demo_quota_reached',
  /** 02/10/2026 : renvoi du lien de suivi impossible, la fiche client n'a pas d'adresse e-mail (409). */
  noCustomerEmail: 'no_customer_email',
  /** 02/10/2026 : renvoi du lien de suivi impossible, le client s'est opposé aux e-mails (409). */
  customerEmailOptOut: 'customer_email_opt_out',
  /** 02/10/2026 : dossier clôturé, le lien de suivi n'a plus cours ; partager le compte rendu (409). */
  interventionClosed: 'intervention_closed',
  /** 02/10/2026 : trop de renvois du lien de suivi pour ce dossier (429, en-tête `Retry-After`). */
  tooManyRequests: 'too_many_requests',
} as const;

// ─── Console plateforme ─────────────────────────────────────────────────────

/** Métriques d'un garage : **uniquement des comptages** (fonction SQL `platform_garage_metrics`). */
export const PlatformGarageMetrics = z.object({
  interventionsOpen: z.int().nonnegative(),
  interventionsTotal: z.int().nonnegative(),
  members: z.int().nonnegative(),
  /** Membres dont une session sur ce garage a servi dans les 7 derniers jours. */
  membersActive7d: z.int().nonnegative(),
  photos: z.int().nonnegative(),
  /** Octets des fichiers d'origine des photos et des photos de fiche (copies dérivées non comptées). */
  photoBytes: z.int().nonnegative(),
  emailsSent30d: z.int().nonnegative(),
  emailsFailed30d: z.int().nonnegative(),
  lastActivityAt: Timestamp.nullable(),
});
export type PlatformGarageMetrics = z.infer<typeof PlatformGarageMetrics>;

export const PlatformGarageOutput = z.object({
  slug: GarageSlug,
  name: z.string(),
  status: GarageStatus,
  createdAt: Timestamp,
  suspendedAt: Timestamp.nullable(),
  suspendedReason: z.string().nullable(),
  /** URL publique du logo (déjà publique : e-mails, portail), ou `null`. */
  logoUrl: z.string().nullable(),
  /** Propriétaire (compte d'équipe) ou invitation du propriétaire en attente. */
  owner: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
  ownerInvitationPending: z.boolean(),
  metrics: PlatformGarageMetrics,
  /**
   * Vague 7 : `customer` ou `demo` (garage de démonstration : jamais compté comme un client, exclu des totaux
   * et de toute facturation ; la console les présente à part).
   */
  kind: GarageKind,
});
export type PlatformGarageOutput = z.infer<typeof PlatformGarageOutput>;

export const PlatformGarageParams = z.strictObject({ slug: GarageSlug });

/** Fuseau horaire IANA reconnu par le moteur JavaScript. */
const TimeZone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('fr-FR', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Fuseau horaire inconnu');

/**
 * Identité et marque d'un garage, modifiables par la plateforme (vague 5, A2) : jamais le contenu des
 * dossiers. Tout est enregistré dans `garages`, `garage_branding` et `garage_settings`.
 */
export const PlatformGarageIdentityFields = z.strictObject({
  /** Nom du garage dans la console et à l'invitation. */
  name: ShortText,
  /** Nom affiché au client (marque) ; par défaut, `name`. */
  displayName: ShortText,
  /** Nom de l'expéditeur des e-mails ; par défaut, `displayName`. */
  senderDisplayName: ShortText,
  address: LongText.max(300).nullable(),
  phone: Phone.nullable(),
  openingHours: LongText.max(300).nullable(),
  /** Adresse de réponse des e-mails ; par défaut, l'e-mail du propriétaire. */
  replyToEmail: Email.nullable(),
  /** Destinataires des notifications du garage ; par défaut, l'e-mail du propriétaire. */
  notificationEmails: z.array(Email).max(10),
  countryCode: CountryCode,
  timezone: TimeZone,
  primaryColor: HexColor,
  secondaryColor: HexColor,
  /** Vague 6 : 3e couleur (ou `null`) et mode de présence de la marque. */
  tertiaryColor: HexColor.nullable(),
  brandMode: BrandMode,
});

/** Réglages initiaux facultatifs (valeurs par défaut de DECISIONS.md sinon), modifiables ensuite par le garage. */
export const PlatformGarageInitialSettings = z.strictObject({
  storageFreeDays: z.int().min(0).max(365).optional(),
  storageDayType: StorageDayType.optional(),
  /** Tarif journalier TTC du gardiennage, ou `null` (aucun). */
  storageDailyFee: Money.nullable().optional(),
  storageTermsText: LongText.optional(),
  reportLinkTtlDays: z.int().min(30).max(3650).optional(),
  portalAccessAfterReleaseHours: z.int().min(0).max(24 * 90).optional(),
});

export const PlatformGarageCreateInput = z.strictObject({
  ...PlatformGarageIdentityFields.partial().shape,
  name: ShortText,
  /** Facultatif : sinon dérivé du nom (`slugify`), suffixé en cas de collision ; demandé et pris : 409. */
  slug: GarageSlug.optional(),
  ownerFirstName: ShortText.max(100),
  ownerLastName: ShortText.max(100),
  ownerEmail: Email,
  settings: PlatformGarageInitialSettings.optional(),
  /** `false` : aucun e-mail ; le lien d'invitation est alors renvoyé **une seule fois** dans la réponse. */
  sendEmail: z.boolean().default(true),
});
/**
 * Champs fixés à la création seulement (audit S29) : destinataires des notifications du garage et adresse
 * de réponse des e-mails aux clients. Ces e-mails portent le contenu des dossiers : après la création,
 * seul le propriétaire les change (Compte → Réglages), jamais la console plateforme ni
 * `platform:update-garage`.
 */
export const PLATFORM_CREATION_ONLY_FIELDS = ['replyToEmail', 'notificationEmails'] as const;

/**
 * Modification partielle (au moins un champ) de l'identité et de la marque par la plateforme.
 * `replyToEmail` et `notificationEmails` sont **refusés** (400) après la création (S29). Ils restent dans
 * le type le temps que la console web les retire de son formulaire.
 */
export const PlatformGarageIdentityInput = PlatformGarageIdentityFields.partial()
  .refine((v) => Object.keys(v).length > 0, 'Aucune modification')
  .refine((v) => PLATFORM_CREATION_ONLY_FIELDS.every((k) => v[k] === undefined), {
    message: 'Destinataires des notifications et adresse de réponse : réglés par le propriétaire du garage seulement',
    path: ['notificationEmails'],
  });

/** Identité et marque actuelles (fiche de la console). */
export const PlatformGarageIdentity = z.object({
  name: z.string(),
  displayName: z.string(),
  senderDisplayName: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  replyToEmail: z.string().nullable(),
  notificationEmails: z.array(z.string()),
  countryCode: z.string(),
  timezone: z.string(),
  primaryColor: HexColor,
  secondaryColor: HexColor,
  tertiaryColor: HexColor.nullable(),
  brandMode: BrandMode,
  logoUrl: z.string().nullable(),
  iconUrl: z.string().nullable(),
});
export type PlatformGarageIdentity = z.infer<typeof PlatformGarageIdentity>;

/** Fiche d'un garage dans la console : comptages et identité (aucun contenu de dossier). */
export const PlatformGarageDetail = PlatformGarageOutput.extend({
  identity: PlatformGarageIdentity,
  /**
   * Vague 7 : modules actifs du garage (lecture seule pour la console ; base d'une future facturation par
   * module, sans facturation aujourd'hui) et garage de démonstration (`garages.is_demo`).
   */
  enabledModules: z.array(ModuleKey),
  /** Vague 7 : réglages d'un garage de démonstration (`null` pour un garage client). */
  demo: z
    .object({
      /** Réinitialisé chaque nuit (défaut) ou conservé (le prospect retrouve ce qu'il a essayé). */
      resetNightly: z.boolean(),
      specialties: z.array(DemoSpecialty),
      /** Liens actifs (non révoqués, non expirés). */
      activeLinks: z.int().nonnegative(),
    })
    .nullable(),
});
export type PlatformGarageDetail = z.infer<typeof PlatformGarageDetail>;

export const PlatformGarageCreateOutput = z.object({
  garage: PlatformGarageDetail,
  /**
   * Toujours `null` (audit S41) : la console ne reçoit jamais le lien d'invitation brut. Avec
   * `sendEmail = false`, l'invitation est créée sans envoi ; « Renvoyer l'invitation »
   * (`platform.garages.resendOwnerInvitation`) l'envoie au propriétaire. Champ gardé (type nullable) le
   * temps que la console web retire l'affichage du lien.
   */
  invitationUrl: z.string().nullable(),
});

export const PlatformReasonInput = z.strictObject({ reason: z.string().trim().min(3).max(500) });
export const PlatformUpdateSlugInput = z.strictObject({ slug: GarageSlug });

// ─── Accès support avec consentement ────────────────────────────────────────

/** Durées que le propriétaire peut accorder (minutes) : 1 h, 4 h, 24 h, 72 h, 7 jours. */
export const SUPPORT_DURATIONS_MINUTES = [60, 240, 1440, 4320, 10_080] as const;
export const SupportDuration = z.union(SUPPORT_DURATIONS_MINUTES.map((m) => z.literal(m)) as [
  z.ZodLiteral<60>,
  z.ZodLiteral<240>,
  z.ZodLiteral<1440>,
  z.ZodLiteral<4320>,
  z.ZodLiteral<10_080>,
]);
export type SupportDuration = z.infer<typeof SupportDuration>;

export const SupportStatus = z.enum(['pending', 'granted', 'refused', 'cancelled', 'expired', 'revoked', 'ended']);
export type SupportStatus = z.infer<typeof SupportStatus>;
export const SupportScope = z.enum(SUPPORT_SCOPES);
export type SupportScope = z.infer<typeof SupportScope>;
/** Motif de fin d'un accès accordé : échéance, retrait par le propriétaire, fin par le support. */
export const SupportEndReason = z.enum(['expired', 'revoked', 'ended']);
export type SupportEndReason = z.infer<typeof SupportEndReason>;

/** Accès support tel que vu par le garage (`support.list`) et par la plateforme. */
export const SupportAccessOutput = z.object({
  id: Id,
  status: SupportStatus,
  reason: z.string(),
  requestedDurationMinutes: z.int(),
  /** Nom de l'administrateur plateforme qui a fait la demande (affiché « Support · <nom> »). */
  requestedByName: z.string().nullable(),
  requestedAt: Timestamp,
  scope: SupportScope,
  amountsVisible: z.boolean(),
  decidedByName: z.string().nullable(),
  decidedAt: Timestamp.nullable(),
  grantedUntil: Timestamp.nullable(),
  endedAt: Timestamp.nullable(),
  endReason: SupportEndReason.nullable(),
});
export type SupportAccessOutput = z.infer<typeof SupportAccessOutput>;

export const PlatformSupportAccessOutput = SupportAccessOutput.extend({
  garage: z.object({ slug: GarageSlug, name: z.string(), status: GarageStatus }),
});

export const SupportRequestInput = z.strictObject({
  /** Seule désignation d'un garage par le corps d'une requête : espace plateforme uniquement. */
  garageSlug: GarageSlug,
  reason: z.string().trim().min(10).max(1000),
  requestedDurationMinutes: SupportDuration,
});
export const SupportAcceptInput = z.strictObject({
  durationMinutes: SupportDuration,
  scope: SupportScope.default('read_only'),
  amountsVisible: z.boolean().default(false),
});
/** `support.list`, `platform.support.list` : tous les accès (récents d'abord), ou ceux d'un statut. */
export const SupportListQuery = z.strictObject({ status: SupportStatus.optional() });
export const SupportEndInput =z.strictObject({ note: z.string().trim().max(500).optional() });

/** Accès support **en cours** dans le garage actif (bandeau visible de toute l'équipe). */
export const ActiveSupportAccess = z.object({
  id: Id,
  scope: SupportScope,
  amountsVisible: z.boolean(),
  grantedUntil: Timestamp,
});
export type ActiveSupportAccess = z.infer<typeof ActiveSupportAccess>;

/**
 * Journal d'un accès support (`support.activity`) : toutes les actions faites pendant cet accès
 * (entrée, consultations qui modifient, décisions…), au format du journal du dossier, avec le dossier
 * concerné. Lien de l'e-mail « Le support a rendu l'accès ».
 */
export const SupportActivityEntry = ActivityEntry.extend({ interventionId: Id.nullable() });
export type SupportActivityEntry = z.infer<typeof SupportActivityEntry>;

/**
 * Identité publique de la plateforme (`public.platform`), lue par les pages sans session (connexion,
 * mot de passe oublié, invitation, erreurs). Nom du produit seulement (`PRODUCT_NAME`) : aucune donnée
 * personnelle ni de garage. Changer de nom = changer la configuration.
 */
export const PublicPlatformOutput = z.strictObject({
  productName: z.string().min(1),
  /** Face garage (décision du 04/10/2026) : connexion de l'équipe, console, démonstration. */
  productNamePro: z.string().min(1),
  /** Vague 7 : démonstration ouverte (`DEMO_ENABLED`) : bouton « Voir la démo » sur la page de connexion. */
  demoEnabled: z.boolean().optional(),
});
export type PublicPlatformOutput = z.infer<typeof PublicPlatformOutput>;

// ─── Vague 7 : démonstrations (console) ─────────────────────────────────────

/**
 * « Créer une démo pour un prospect » : un garage de démonstration aux couleurs du prospect (logo et icône
 * ensuite par `platform.garages.uploadLogo` / `uploadIcon`), rempli par le jeu de démonstration, et un
 * premier lien. Le slug est dérivé du nom s'il est absent (suffixé en cas de collision).
 */
export const PlatformDemoLinkCreateInput = z.strictObject({
  /** Repère interne de la console (« Rendez-vous du 12 »), jamais montré au prospect. */
  label: ShortText.max(100).optional(),
  expiresInDays: z.int().min(1).max(DEMO_LIMITS.linkMaxDays).default(DEMO_LIMITS.linkDefaultDays),
  defaultRole: DemoRole.default('owner'),
});

export const PlatformDemoCreateInput = z.strictObject({
  name: ShortText,
  slug: GarageSlug.optional(),
  displayName: ShortText.optional(),
  primaryColor: HexColor.optional(),
  secondaryColor: HexColor.optional(),
  tertiaryColor: HexColor.nullable().optional(),
  brandMode: BrandMode.default('discreet'),
  specialties: z.array(DemoSpecialty).max(6).default([]),
  /** `false` : « ne pas réinitialiser » (les essais du prospect sont gardés). */
  resetNightly: z.boolean().default(true),
  link: PlatformDemoLinkCreateInput.default({ expiresInDays: DEMO_LIMITS.linkDefaultDays, defaultRole: 'owner' }),
});

export const PlatformDemoUpdateInput = z
  .strictObject({ resetNightly: z.boolean().optional(), specialties: z.array(DemoSpecialty).max(6).optional() })
  .refine((v) => Object.keys(v).length > 0, 'Aucune modification');

/** Lien de démonstration (console) : jamais le jeton, sauf à la création (`url`, montrée une fois). */
export const DemoLinkOutput = z.object({
  id: Id,
  label: z.string().nullable(),
  defaultRole: DemoRole,
  createdAt: Timestamp,
  createdByName: z.string().nullable(),
  expiresAt: Timestamp,
  revokedAt: Timestamp.nullable(),
  /** Comptage simple : dernière ouverture et nombre d'ouvertures (aucun suivi individuel). */
  lastOpenedAt: Timestamp.nullable(),
  openCount: z.int().nonnegative(),
  status: z.enum(['active', 'expired', 'revoked']),
});
export type DemoLinkOutput = z.infer<typeof DemoLinkOutput>;

/** Création d'un lien : l'adresse complète (jeton dans le fragment) n'est renvoyée **qu'une fois**. */
export const DemoLinkCreateOutput = z.object({ link: DemoLinkOutput, url: z.string() });

export const PlatformDemoCreateOutput = z.object({ garage: PlatformGarageDetail, link: DemoLinkCreateOutput });
export type PlatformDemoCreateOutput = z.infer<typeof PlatformDemoCreateOutput>;

export const PlatformDemoLinkParams = z.strictObject({ slug: GarageSlug, id: Id });

// ─── 05/10/2026 : contenu d'une démonstration (remplir, vider, convertir) ───

/** Contenu actuel d'un garage de démonstration (console). */
export const PlatformDemoContent = z.object({
  customers: z.int().nonnegative(),
  vehicles: z.int().nonnegative(),
  interventions: z.int().nonnegative(),
  photos: z.int().nonnegative(),
  messages: z.int().nonnegative(),
  agendaItems: z.int().nonnegative(),
});
export type PlatformDemoContent = z.infer<typeof PlatformDemoContent>;

/**
 * « Remplir avec des données de test » : jeu, volume, planning d'agenda ; `add` ajoute au contenu existant,
 * `replace` le vide d'abord. Toujours fictif, aucun e-mail.
 */
export const PlatformDemoFillInput = z.strictObject({
  dataset: DemoDataset,
  volume: DemoVolume.default('standard'),
  agenda: z.boolean().default(true),
  mode: z.enum(['add', 'replace']).default('replace'),
});
export type PlatformDemoFillInput = z.input<typeof PlatformDemoFillInput>;

export const PlatformDemoFillOutput = z.object({
  content: PlatformDemoContent,
  /** Ajouté par ce remplissage. */
  added: z.object({
    customers: z.int(),
    vehicles: z.int(),
    interventions: z.int(),
    photos: z.int(),
    agendaItems: z.int(),
  }),
});
export type PlatformDemoFillOutput = z.infer<typeof PlatformDemoFillOutput>;

/**
 * « Convertir en vrai garage » : irréversible. `confirmSlug` doit reprendre l'adresse du garage (confirmation
 * explicite) ; le propriétaire réel est invité comme à la création d'un garage.
 */
export const PlatformDemoConvertInput = z.strictObject({
  confirmSlug: GarageSlug,
  ownerFirstName: ShortText.max(100),
  ownerLastName: ShortText.max(100),
  ownerEmail: Email,
  /** `false` : invitation créée sans envoi (« Renvoyer l'invitation » ensuite). */
  sendEmail: z.boolean().default(true),
});
export type PlatformDemoConvertInput = z.input<typeof PlatformDemoConvertInput>;

export const PlatformDemoConvertOutput = z.object({
  garage: PlatformGarageDetail,
  removed: PlatformDemoContent.extend({ links: z.int().nonnegative(), demoAccounts: z.int().nonnegative() }),
  invitationSent: z.boolean(),
});
export type PlatformDemoConvertOutput = z.infer<typeof PlatformDemoConvertOutput>;
